import {
  collection,
  collectionGroup,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { SmeTask } from "../types";

/**
 * Creates an SME routing task in the opportunity's sme_tasks subcollection
 * and updates the assigned requirement's assignedTo/assignedToEmail fields.
 */
export async function createSmeTask(
  opportunityId: string,
  taskData: {
    opportunityTitle: string;
    clientName: string;
    requirementId: string;
    requirementTitle: string;
    requirementText: string;
    question: string;
    assigneeId: string;
    assigneeName: string;
    assigneeEmail: string;
    dueDate?: string;
  },
): Promise<string> {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("You must be signed in to create an SME task.");
  }

  const tasksColRef = collection(db, "opportunities", opportunityId, "sme_tasks");
  const taskDocRef = doc(tasksColRef);

  const now = new Date().toISOString();
  const newTask: SmeTask = {
    id: taskDocRef.id,
    opportunityId,
    opportunityTitle: taskData.opportunityTitle,
    clientName: taskData.clientName,
    requirementId: taskData.requirementId,
    requirementTitle: taskData.requirementTitle,
    requirementText: taskData.requirementText,
    question: taskData.question,
    assigneeId: taskData.assigneeId,
    assigneeName: taskData.assigneeName,
    assigneeEmail: taskData.assigneeEmail,
    requestedBy: currentUser.displayName || currentUser.email,
    requestedByEmail: currentUser.email,
    dueDate: taskData.dueDate,
    status: "open",
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(taskDocRef, newTask);

  // Sync assignment onto requirement subcollection document
  try {
    const reqDocRef = doc(
      db,
      "opportunities",
      opportunityId,
      "requirements",
      taskData.requirementId,
    );
    await updateDoc(reqDocRef, {
      assignedTo: taskData.assigneeName,
      assignedToEmail: taskData.assigneeEmail,
      updatedAt: now,
    });
  } catch (err) {
    console.warn(`Could not update requirement ${taskData.requirementId} assignedTo fields:`, err);
  }

  return taskDocRef.id;
}

/**
 * Subscribes to all SME tasks for a specific opportunity
 */
export function subscribeToOpportunitySmeTasks(
  opportunityId: string,
  callback: (tasks: SmeTask[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "sme_tasks");
  const q = query(colRef, orderBy("createdAt", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const tasks: SmeTask[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          opportunityTitle: data.opportunityTitle || "",
          clientName: data.clientName || "",
          requirementId: data.requirementId || "",
          requirementTitle: data.requirementTitle || "",
          requirementText: data.requirementText || "",
          question: data.question || "",
          assigneeId: data.assigneeId || "",
          assigneeName: data.assigneeName || "",
          assigneeEmail: data.assigneeEmail || "",
          requestedBy: data.requestedBy || "",
          requestedByEmail: data.requestedByEmail || "",
          dueDate: data.dueDate,
          status: data.status || "open",
          response: data.response,
          respondedAt: data.respondedAt,
          resolvedAt: data.resolvedAt,
          resolvedBy: data.resolvedBy,
          returnReason: data.returnReason,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        };
      });
      callback(tasks);
    },
    (err) => {
      console.error(`Error subscribing to SME tasks for opportunity ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Subscribes to all SME tasks assigned to a specific user across all opportunities (Review Queue)
 */
export function subscribeToUserSmeTasks(
  userEmail: string,
  callback: (tasks: SmeTask[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const tasksGroup = collectionGroup(db, "sme_tasks");
  const q = query(
    tasksGroup,
    where("assigneeEmail", "==", userEmail),
    orderBy("createdAt", "desc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tasks: SmeTask[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId: data.opportunityId || "",
          opportunityTitle: data.opportunityTitle || "",
          clientName: data.clientName || "",
          requirementId: data.requirementId || "",
          requirementTitle: data.requirementTitle || "",
          requirementText: data.requirementText || "",
          question: data.question || "",
          assigneeId: data.assigneeId || "",
          assigneeName: data.assigneeName || "",
          assigneeEmail: data.assigneeEmail || "",
          requestedBy: data.requestedBy || "",
          requestedByEmail: data.requestedByEmail || "",
          dueDate: data.dueDate,
          status: data.status || "open",
          response: data.response,
          respondedAt: data.respondedAt,
          resolvedAt: data.resolvedAt,
          resolvedBy: data.resolvedBy,
          returnReason: data.returnReason,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        };
      });
      callback(tasks);
    },
    (err) => {
      console.error(`Error subscribing to collectionGroup SME tasks for ${userEmail}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Submits an answer from the assigned SME
 */
export async function submitSmeTaskResponse(
  opportunityId: string,
  taskId: string,
  response: string,
): Promise<void> {
  const taskRef = doc(db, "opportunities", opportunityId, "sme_tasks", taskId);
  const now = new Date().toISOString();
  await updateDoc(taskRef, {
    response: response.trim(),
    respondedAt: now,
    status: "submitted",
    updatedAt: now,
  });
}

/**
 * Accepts an SME answer (e.g. by proposal owner, requester, or admin)
 */
export async function acceptSmeTask(
  opportunityId: string,
  taskId: string,
  resolvedBy: string,
): Promise<void> {
  const taskRef = doc(db, "opportunities", opportunityId, "sme_tasks", taskId);
  const now = new Date().toISOString();
  await updateDoc(taskRef, {
    status: "accepted",
    resolvedAt: now,
    resolvedBy,
    updatedAt: now,
  });
}

/**
 * Returns an SME task for revision or clarification
 */
export async function returnSmeTask(
  opportunityId: string,
  taskId: string,
  returnReason: string,
  resolvedBy: string,
): Promise<void> {
  const taskRef = doc(db, "opportunities", opportunityId, "sme_tasks", taskId);
  const now = new Date().toISOString();
  await updateDoc(taskRef, {
    status: "returned",
    returnReason: returnReason.trim(),
    resolvedAt: now,
    resolvedBy,
    updatedAt: now,
  });
}

/**
 * Deletes an SME task (requester or admin)
 */
export async function deleteSmeTask(opportunityId: string, taskId: string): Promise<void> {
  const taskRef = doc(db, "opportunities", opportunityId, "sme_tasks", taskId);
  await deleteDoc(taskRef);
}
